import * as React from 'react'
import { Node, type JSONContent } from '@tiptap/core'
import Link from '@tiptap/extension-link'
import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { BlastEditorToolbar, type BlastEditorTool } from '@/components/ui/blast-editor-toolbar'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { ImageUpload, type UploadedImage } from '@/components/ui/image-upload'
import { Label } from '@/components/ui/label'
import { postFormData } from '@/lib/api'

const BlastButton = Node.create({
  name: 'button',
  group: 'block',
  atom: true,
  addAttributes() {
    return { href: { default: '' }, label: { default: 'Button' } }
  },
  parseHTML() {
    return [{ tag: 'a[data-blast-button]' }]
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'a',
      {
        'data-blast-button': 'true',
        href: HTMLAttributes.href,
        class:
          'inline-flex rounded-md bg-primary px-4 py-2 text-body-sm font-medium text-primary-foreground',
      },
      HTMLAttributes.label,
    ]
  },
})

const BlastImage = Node.create({
  name: 'image',
  group: 'block',
  atom: true,
  addAttributes() {
    return { uploadId: { default: null }, src: { default: '' }, alt: { default: '' } }
  },
  parseHTML() {
    return [{ tag: 'img[data-blast-image]' }]
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'img',
      {
        'data-blast-image': 'true',
        'data-upload-id': HTMLAttributes.uploadId,
        src: HTMLAttributes.src,
        alt: HTMLAttributes.alt,
        class: 'my-2 h-auto w-full max-w-full object-contain',
      },
    ]
  },
})

const BlastMergeField = Node.create({
  name: 'mergeField',
  group: 'inline',
  inline: true,
  atom: true,
  addAttributes() {
    return { name: { default: '' }, label: { default: '' } }
  },
  parseHTML() {
    return [{ tag: 'span[data-blast-merge-field]' }]
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      {
        'data-blast-merge-field': HTMLAttributes.name,
        class: 'rounded bg-accent px-1 text-foreground',
      },
      `{{${HTMLAttributes.label}}}`,
    ]
  },
})

interface UploadResponse {
  upload: { id: number; filename: string; url: string }
}

export interface BlastMergeFieldOption {
  name: string
  label: string
  group?: string
}

interface BlastRichTextEditorProps {
  value: JSONContent
  onChange: (value: JSONContent) => void
  beforeContent?: React.ReactNode
  afterContent?: React.ReactNode
  mergeFields?: BlastMergeFieldOption[]
}

export function BlastRichTextEditor({
  value,
  onChange,
  beforeContent,
  afterContent,
  mergeFields = [],
}: BlastRichTextEditorProps) {
  const [tool, setTool] = React.useState<BlastEditorTool | null>(null)
  const [label, setLabel] = React.useState('')
  const [href, setHref] = React.useState('https://')
  const [imageFile, setImageFile] = React.useState<File | null>(null)
  const [imageError, setImageError] = React.useState<string | null>(null)
  const [imagePending, setImagePending] = React.useState(false)
  const imagePreview = useLocalImagePreview(imageFile)
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] }, link: false }),
      Link.configure({ openOnClick: false, autolink: false }),
      BlastButton,
      BlastImage,
      BlastMergeField,
    ],
    content: value,
    immediatelyRender: false,
    onUpdate: ({ editor: current }) => onChange(current.getJSON()),
    editorProps: {
      attributes: {
        class:
          'min-h-52 px-8 py-6 text-body-sm leading-relaxed text-foreground outline-none [&_h1]:mb-3 [&_h1]:text-heading-lg [&_h1]:font-normal [&_h2]:mb-3 [&_h2]:text-heading [&_h2]:font-normal [&_h3]:mb-2 [&_h3]:text-heading-sm [&_p]:mb-4 [&_a:not([data-blast-button])]:text-primary [&_a:not([data-blast-button])]:underline [&_hr]:my-5 [&_hr]:border-border',
        'aria-label': 'Blast body',
        'aria-multiline': 'true',
        role: 'textbox',
      },
    },
  })

  React.useEffect(() => {
    if (!editor || editor.isFocused || JSON.stringify(editor.getJSON()) === JSON.stringify(value))
      return
    editor.commands.setContent(value)
  }, [editor, value])

  if (!editor) return <div className="min-h-52 px-8 py-6" aria-label="Loading editor" />

  function openTool(nextTool: BlastEditorTool) {
    setTool(nextTool)
    setLabel('')
    setImageFile(null)
    setImageError(null)
    setHref(
      nextTool === 'link'
        ? (editor?.getAttributes('link').href as string) || 'https://'
        : 'https://',
    )
  }

  function addMergeField(option: BlastMergeFieldOption) {
    if (!editor) return

    editor.chain().focus().insertContent({ type: 'mergeField', attrs: option }).run()
    setTool(null)
  }

  async function addImage() {
    if (!editor || !imageFile || !label.trim()) return

    setImagePending(true)
    setImageError(null)
    try {
      const formData = new FormData()
      formData.append('file', imageFile)
      const { upload } = await postFormData<UploadResponse>('/api/v1/uploads', formData)
      editor
        .chain()
        .focus()
        .insertContent({
          type: 'image',
          attrs: { uploadId: upload.id, src: upload.url, alt: label.trim() },
        })
        .run()
      setTool(null)
    } catch (error) {
      setImageError(error instanceof Error ? error.message : 'Upload failed')
    } finally {
      setImagePending(false)
    }
  }

  function applyTool() {
    if (!editor || !tool || !href.trim()) return
    if (tool === 'link') editor.chain().focus().extendMarkRange('link').setLink({ href }).run()
    else if (label.trim())
      editor.chain().focus().insertContent({ type: 'button', attrs: { label, href } }).run()
    setTool(null)
  }

  return (
    <>
      <div>
        <BlastEditorToolbar editor={editor} onOpenTool={openTool} />
        <div className="bg-muted/40 p-8">
          <div className="mx-auto max-w-xl rounded-md border border-border bg-background">
            {beforeContent}
            <EditorContent editor={editor} />
            {afterContent}
          </div>
        </div>
      </div>
      <EditorToolDialog
        tool={tool}
        label={label}
        href={href}
        imageFile={imageFile}
        imagePreview={imagePreview}
        imagePending={imagePending}
        imageError={imageError}
        mergeFields={mergeFields}
        onClose={() => setTool(null)}
        onLabelChange={setLabel}
        onHrefChange={setHref}
        onImageChoose={setImageFile}
        onApply={tool === 'image' ? addImage : applyTool}
        onMergeField={addMergeField}
      />
    </>
  )
}

function EditorToolDialog({
  tool,
  label,
  href,
  imageFile,
  imagePreview,
  imagePending,
  imageError,
  mergeFields,
  onClose,
  onLabelChange,
  onHrefChange,
  onImageChoose,
  onApply,
  onMergeField,
}: {
  tool: BlastEditorTool | null
  label: string
  href: string
  imageFile: File | null
  imagePreview: UploadedImage | null
  imagePending: boolean
  imageError: string | null
  mergeFields: BlastMergeFieldOption[]
  onClose: () => void
  onLabelChange: (value: string) => void
  onHrefChange: (value: string) => void
  onImageChoose: (file: File | null) => void
  onApply: () => void
  onMergeField: (option: BlastMergeFieldOption) => void
}) {
  return (
    <Dialog open={tool !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{toolTitle(tool)}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {tool === 'mergeField' ? (
            <div className="max-h-72 space-y-1 overflow-y-auto">
              {mergeFields.map((option) => (
                <Button
                  key={option.name}
                  type="button"
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => onMergeField(option)}
                >
                  {option.label}
                </Button>
              ))}
            </div>
          ) : tool === 'image' ? (
            <>
              <ImageUpload
                image={imagePreview}
                label="Image file"
                pending={imagePending}
                onChoose={onImageChoose}
                onRemove={() => onImageChoose(null)}
              />
              <div className="space-y-1.5">
                <Label htmlFor="blast-image-alt">Alt text</Label>
                <Input
                  id="blast-image-alt"
                  value={label}
                  onChange={(event) => onLabelChange(event.target.value)}
                />
              </div>
              {imageError ? <p className="text-body-sm text-destructive">{imageError}</p> : null}
            </>
          ) : tool === 'button' ? (
            <div className="space-y-1.5">
              <Label htmlFor="blast-button-label">Label</Label>
              <Input
                id="blast-button-label"
                value={label}
                onChange={(event) => onLabelChange(event.target.value)}
              />
            </div>
          ) : null}
          {tool !== 'image' && tool !== 'mergeField' ? (
            <div className="space-y-1.5">
              <Label htmlFor="blast-link-url">URL</Label>
              <Input
                id="blast-link-url"
                value={href}
                onChange={(event) => onHrefChange(event.target.value)}
              />
            </div>
          ) : null}
        </div>
        {tool !== 'mergeField' ? (
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              disabled={tool === 'image' && (!imageFile || !label.trim() || imagePending)}
              onClick={onApply}
            >
              {tool === 'image' ? 'Add image' : 'Add'}
            </Button>
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

function toolTitle(tool: BlastEditorTool | null) {
  if (tool === 'image') return 'Add image'
  if (tool === 'button') return 'Add button'
  if (tool === 'mergeField') return 'Insert merge field'
  return 'Add link'
}

function useLocalImagePreview(file: File | null): UploadedImage | null {
  const [preview, setPreview] = React.useState<UploadedImage | null>(null)

  React.useEffect(() => {
    if (!file || typeof URL.createObjectURL !== 'function') {
      setPreview(null)
      return
    }

    const url = URL.createObjectURL(file)
    setPreview({ filename: file.name, url })
    return () => URL.revokeObjectURL(url)
  }, [file])

  return preview
}
